import create from 'zustand'
import { getPlanLimits } from '../api/billingApi'
import { Logger } from '../logger'

// The plan's limits at the active center and what is used (GET /v1/billing/limits, 0173): clients per
// location, client users per body shop, repair orders this month, locked ROs. Loaded by the pages that
// show usage (Clients, Billing) and refreshed after anything that changes it (a client added, an upgrade).
const initialState = {
  tier: null,
  tierName: null,
  managedExternally: false,
  limits: [],
  lockedRos: 0,
  // Clients the plan keeps enabled (the first N), or null when there is no client limit.
  enabledClientIds: null,
  loadedForCcId: null,
  isLoading: false,
  error: null
}

const useLimitStore = create((set, get) => ({
  ...initialState,

  reset: () => set({ ...initialState }),

  loadLimits: async (ccId, { force = false } = {}) => {
    if (!ccId) {
      set({ ...initialState })
      return
    }
    const current = get()
    if (!force && current.loadedForCcId === ccId && !current.error) return
    set(current.loadedForCcId === ccId ? { isLoading: true } : { ...initialState, isLoading: true })
    try {
      const view = await getPlanLimits(ccId)
      set({
        ...initialState,
        tier: view?.tier ?? null,
        tierName: view?.tierName ?? null,
        managedExternally: Boolean(view?.managedExternally),
        limits: Array.isArray(view?.limits) ? view.limits : [],
        lockedRos: Number(view?.lockedRos) || 0,
        enabledClientIds: Array.isArray(view?.enabledClientIds) ? view.enabledClientIds : null,
        loadedForCcId: ccId
      })
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'LimitStore', method: 'loadLimits', ccId } })
      set({ ...initialState, loadedForCcId: ccId, error: error?.message || 'error' })
    }
  }
}))

/** One limit's status by key, or undefined when the plan has no such kind. */
export const limitByKey = (state, key) => (state?.limits || []).find((l) => l.key === key)

/** Whether a client (by id) is paused by the plan's client limit: not among the first N when there is a limit. */
export const isClientPausedByPlan = (state, clientId) =>
  Array.isArray(state?.enabledClientIds) && clientId != null && !state.enabledClientIds.includes(Number(clientId))

export { useLimitStore }
export default useLimitStore
